// FieldDesk — детальный просмотр визита: карточка + timeline (visit_events) +
// отмена (через POST /api/visits/:id/cancel — матрица прав обеспечивается на backend).

import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import type { VisitWithEvents } from '../api/types';
import { TYPE_COLOR, TYPE_LABEL } from '../styles/theme';
import { StatusBadge } from '../components/Badge';
import { Avatar } from '../components/Avatar';
import { ErrorStrip } from './Dashboard';

interface Props {
  visitId: number;
  onClose(): void;
}

export function VisitDetailDrawer({ visitId, onClose }: Props) {
  const { api, state } = useAuth();
  const [visit, setVisit] = useState<VisitWithEvents | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const v = await api.getVisit(visitId);
      setVisit(v);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api, visitId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function doCancel() {
    if (!reason.trim()) return;
    setCancelling(true);
    setErr(null);
    try {
      await api.cancelVisit(visitId, reason.trim());
      setShowCancel(false);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setCancelling(false);
    }
  }

  // Кнопка «Отменить выезд» — зеркалит backend-матрицу canCancelVisit():
  //   ADMIN — любой визит в любом статусе (кроме уже CANCELLED).
  //   DISPATCHER / ENGINEER — любой визит, но только в статусе PLANNED.
  const currentUser = state.status === 'authenticated' ? state.user : null;
  const role = currentUser?.role ?? null;
  const canCancel =
    visit != null &&
    visit.status !== 'CANCELLED' &&
    (role === 'ADMIN' ||
      ((role === 'DISPATCHER' || role === 'ENGINEER') && visit.status === 'PLANNED'));

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        justifyContent: 'flex-end',
        // Выше Leaflet-control (1000) — иначе на экране Карты деталь визита
        // открывается под картой.
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: '94vw',
          background: '#fff',
          height: '100%',
          overflowY: 'auto',
          boxShadow: '-6px 0 24px rgba(15,23,42,0.15)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Визит</div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>#{visitId}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8' }}>✕</button>
        </div>

        <div style={{ padding: 20 }}>
          {err && <ErrorStrip message={err} />}
          {loading ? (
            <div style={{ color: '#94a3b8' }}>Загрузка…</div>
          ) : visit ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span
                  style={{
                    padding: '3px 8px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    background: (TYPE_COLOR[visit.type] ?? '#64748b') + '18',
                    color: TYPE_COLOR[visit.type] ?? '#64748b',
                  }}
                >
                  {TYPE_LABEL[visit.type] ?? visit.typeLabel}
                </span>
                <StatusBadge status={visit.status} />
              </div>

              <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
                {visit.client?.name ?? (visit.bitrixDealId ? `Сделка #${visit.bitrixDealId}` : 'Без клиента')}
              </h2>

              <SlaBadges visit={visit} />


              <InfoRow label="📅 Дата">
                {formatIsoDate(visit.scheduledDate)} · {visit.scheduledStart}–{visit.scheduledEnd}
              </InfoRow>

              <InfoRow label="👤 Инженер">
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Avatar initials={visit.engineer.initials} color={visit.engineer.color} size={22} />
                  {visit.engineer.fullName}
                </div>
              </InfoRow>

              {visit.client?.address && <InfoRow label="📍 Адрес">{visit.client.address}</InfoRow>}
              {visit.description && <InfoRow label="📝 Описание">{visit.description}</InfoRow>}
              {visit.bitrixDealId && (
                <InfoRow label="🔗 Сделка Б24">
                  <OpenDealLink dealId={visit.bitrixDealId} />
                </InfoRow>
              )}
              {visit.arrivedAt && (
                <InfoRow label="✅ Прибыл">
                  {format(new Date(visit.arrivedAt), 'dd.MM.yyyy HH:mm')}
                  {visit.arrivedLat && ` · ${visit.arrivedLat.toFixed(5)}, ${visit.arrivedLng?.toFixed(5)}`}
                </InfoRow>
              )}
              {visit.finishedAt && (
                <InfoRow label="🏁 Завершён">{format(new Date(visit.finishedAt), 'dd.MM.yyyy HH:mm')}</InfoRow>
              )}
              {visit.cancelReason && <InfoRow label="⛔ Причина отмены">{visit.cancelReason}</InfoRow>}

              <h3 style={{ fontSize: 13, fontWeight: 600, marginTop: 20, marginBottom: 8 }}>История событий</h3>
              <div style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: 14 }}>
                {visit.events.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Событий пока нет.</div>
                ) : (
                  visit.events.map((ev) => {
                    const v = formatEvent(ev.type, ev.payload);
                    return (
                      <div key={ev.id} style={{ marginBottom: 10, position: 'relative' }}>
                        <span
                          style={{
                            position: 'absolute',
                            left: -20,
                            top: 4,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: v.color,
                          }}
                        />
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>
                          <span style={{ marginRight: 6 }} aria-hidden="true">
                            {v.icon}
                          </span>
                          {v.title}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {format(new Date(ev.createdAt), 'dd.MM.yyyy HH:mm')}
                        </div>
                        {v.detail && (
                          <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{v.detail}</div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {canCancel && !showCancel && (
                <button
                  onClick={() => setShowCancel(true)}
                  style={{
                    marginTop: 20,
                    padding: '9px 14px',
                    background: '#fef2f2',
                    color: '#b91c1c',
                    border: '1px solid #fecaca',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  ⛔ Отменить выезд
                </button>
              )}
              {showCancel && (
                <div style={{ marginTop: 16, padding: 14, background: '#fef2f2', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Причина отмены</div>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    placeholder="Укажите причину — она попадёт в timeline сделки Б24"
                    style={{
                      width: '100%',
                      padding: 8,
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 13,
                      resize: 'vertical',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowCancel(false)}
                      style={{ padding: '7px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }}
                    >
                      Назад
                    </button>
                    <button
                      onClick={doCancel}
                      disabled={cancelling || !reason.trim()}
                      style={{
                        padding: '7px 14px',
                        background: reason.trim() ? '#dc2626' : '#fecaca',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {cancelling ? 'Отменяем…' : 'Подтвердить отмену'}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Ссылка/кнопка «Открыть сделку в Битриксе». В embedded — через
// BX24.openPath (slider портала), в standalone — обычный target=_blank на
// домен портала, который backend возвращает при авторизации (sessionStorage
// 'fd.portal'). Хардкод домена убран: на проде портал другой.
function portalDomain(): string | null {
  try {
    return sessionStorage.getItem('fd.portal');
  } catch {
    return null;
  }
}

function OpenDealLink({ dealId }: { dealId: string }) {
  function handleOpen() {
    const path = `/crm/deal/details/${dealId}/`;
    const bx = (window as { BX24?: { openPath?: (p: string) => void } }).BX24;
    if (bx?.openPath) {
      try {
        bx.openPath(path);
        return;
      } catch {
        /* fall through to window.open */
      }
    }
    // Standalone-фолбэк: только если знаем домен портала.
    const domain = portalDomain();
    if (domain) window.open(`https://${domain}${path}`, '_blank', 'noopener,noreferrer');
  }
  return (
    <button
      onClick={handleOpen}
      style={{
        background: 'transparent',
        border: 'none',
        color: '#2563eb',
        fontWeight: 600,
        cursor: 'pointer',
        padding: 0,
        fontSize: 13,
        textDecoration: 'underline',
      }}
      title="Открыть карточку сделки в Битриксе"
    >
      #{dealId} ↗
    </button>
  );
}

// --- Date formatting utility ------------------------------------------------
// Все даты в карточке выезда — единый формат DD.MM.YYYY:
//   - scheduledDate (ISO YYYY-MM-DD) → formatIsoDate
//   - arrivedAt / finishedAt (ISO timestamp) → format(d, 'dd.MM.yyyy HH:mm')
// Раньше «Дата» рисовалась как `2026-05-23 · 09:00–12:00` (ISO), а «Прибыл» —
// как `22.05.2026 20:30` (локальный). UI-ревью: «нужно везде одинаково».
function formatIsoDate(iso: string): string {
  // YYYY-MM-DD → DD.MM.YYYY (без new Date() чтобы не словить TZ-сдвиг).
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

// --- Human-readable event timeline -----------------------------------------
// Парсим payload в осмысленные строки. Сырой JSON никогда не показываем —
// это было главное замечание UX-ревью «не нужно JSON-дамп в истории».
const STATUS_LABEL_LOCAL: Record<string, string> = {
  PLANNED: 'Запланирован',
  EN_ROUTE: 'В пути',
  ON_SITE: 'На месте',
  COMPLETED: 'Завершён',
  CANCELLED: 'Отменён',
};

const TYPE_LABEL_LOCAL: Record<string, string> = {
  INSTALLATION: 'Установка',
  TRAINING: 'Обучение',
  PAID_VISIT: 'Платный выезд',
};

interface FormattedEvent {
  icon: string;
  title: string;
  detail: string | null;
  color: string;
}

function formatEvent(type: string, payload: unknown): FormattedEvent {
  const p = (payload ?? {}) as Record<string, unknown>;
  if (type === 'created') {
    const visitType = String(p.type ?? '');
    const human = TYPE_LABEL_LOCAL[visitType] ?? visitType;
    const time = p.scheduledStart && p.scheduledEnd ? `${p.scheduledStart}–${p.scheduledEnd}` : '';
    return {
      icon: '🕐',
      title: 'Создан',
      detail: human ? `${human}${time ? ` · ${time}` : ''}` : null,
      color: '#94a3b8',
    };
  }
  if (type === 'status_changed') {
    const from = String(p.from ?? '');
    const to = String(p.to ?? '');
    // checkin отдельно показывает прибытие — этот status_changed дублирует.
    if (to === 'ON_SITE' && p.via === 'checkin') {
      return {
        icon: '🟢',
        title: 'На месте',
        detail: null,
        color: '#16a34a',
      };
    }
    if (to === 'CANCELLED') {
      return {
        icon: '⛔',
        title: 'Отменён',
        detail: p.reason ? `Причина: ${String(p.reason)}` : null,
        color: '#dc2626',
      };
    }
    if (to === 'COMPLETED') {
      return { icon: '✅', title: 'Завершён', detail: null, color: '#16a34a' };
    }
    if (to === 'EN_ROUTE') {
      return { icon: '🚗', title: 'В пути', detail: null, color: '#d97706' };
    }
    return {
      icon: '↪',
      title: `${STATUS_LABEL_LOCAL[from] ?? from} → ${STATUS_LABEL_LOCAL[to] ?? to}`,
      detail: null,
      color: '#2563eb',
    };
  }
  if (type === 'updated') {
    const fmtRange = (r: unknown): string | null => {
      const o = r as { scheduledDate?: string; scheduledStart?: string; scheduledEnd?: string } | undefined;
      if (!o?.scheduledDate) return null;
      return `${formatIsoDate(o.scheduledDate)} ${o.scheduledStart ?? ''}–${o.scheduledEnd ?? ''}`;
    };
    const fromStr = fmtRange(p.from);
    const toStr = fmtRange(p.to);
    const detailParts: string[] = [];
    if (fromStr && toStr) detailParts.push(`${fromStr} → ${toStr}`);
    if (p.engineerFrom != null && p.engineerTo != null) detailParts.push('переназначен на другого инженера');
    if (p.descriptionChanged === true) detailParts.push('описание обновлено');
    return {
      icon: '🔄',
      title: 'Изменён',
      detail: detailParts.length > 0 ? detailParts.join(' · ') : null,
      color: '#d97706',
    };
  }
  if (type === 'checkin') {
    const lat = typeof p.latitude === 'number' ? p.latitude : null;
    const lng = typeof p.longitude === 'number' ? p.longitude : null;
    const dist =
      typeof p.deviationDistanceMeters === 'number' ? p.deviationDistanceMeters : null;
    const detailParts: string[] = [];
    if (lat != null && lng != null) detailParts.push(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    if (dist != null) detailParts.push(`расстояние от адреса: ${Math.round(dist)} м`);
    return {
      icon: '📍',
      title: 'Прибыл',
      detail: detailParts.length > 0 ? detailParts.join(' · ') : null,
      color: '#16a34a',
    };
  }
  // Неизвестный тип — пишем имя события без payload (никаких JSON-дампов).
  return { icon: '•', title: type, detail: null, color: '#94a3b8' };
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <span style={{ fontSize: 11, color: '#64748b', marginRight: 6 }}>{label}</span>
      <span style={{ fontSize: 13, color: '#0f172a' }}>{children}</span>
    </div>
  );
}

// SLA-плашки опоздания и отклонения от адреса. Видны всем ролям веб-панели
// (ENGINEER = права DISPATCHER); в Mini App этого экрана нет — инженер там
// видит отдельный warning при чекине.
function SlaBadges({ visit }: { visit: VisitWithEvents }) {
  const { state } = useAuth();
  const canSeeSla = state.status === 'authenticated';
  if (!canSeeSla) return null;

  const showLate = visit.wasLate === true && visit.lateMinutes != null;
  const showDeviation = visit.addressDeviation === true && visit.deviationDistanceMeters != null;
  if (!showLate && !showDeviation) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
      {showLate && (
        <div
          style={{
            padding: '8px 10px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#b91c1c',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          ⏰ Опоздание: {visit.lateMinutes} мин (буфер {visit.latenessBufferMinutes} мин)
        </div>
      )}
      {showDeviation && (
        <div
          style={{
            padding: '8px 10px',
            background: '#fef3c7',
            border: '1px solid #fde68a',
            color: '#92400e',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          📍 Отклонение от адреса: {visit.deviationDistanceMeters} м (норма до{' '}
          {visit.addressDeviationRadiusMeters} м)
        </div>
      )}
    </div>
  );
}
